import { useState, useEffect, useCallback } from 'react';
import { Row, Col, Form, Alert, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import LocationAutocomplete from 'components/common/LocationAutocomplete';
import SuccessModal from 'components/modals/SuccessModal';
import { Coordinates } from 'features/location/googleLocation';
import {
  createShift,
  getShiftById,
  updateShift
} from 'features/shift/shiftApi';
import { getProfile } from 'features/profile/profileApi';
import { useSearchParams } from 'react-router-dom';

const PharmacistCreateShiftPage = () => {
  const [formData, setFormData] = useState({
    location: '',
    address: '',
    postcode: '',
    latitude: 0,
    longitude: 0,
    pay_rate: '',
    required_locums: '',
    locum_role: 'pharmacist',
    summary: '',
    lunch_break_minutes: '',
    travel_expenses: ''
  });

  const [searchParams] = useSearchParams();
  const shiftId = searchParams.get('id');

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shiftRows, setShiftRows] = useState([
    {
      start_date: '',
      start_time: '',
      end_time: ''
    }
  ]);

  useEffect(() => {
    if (!shiftId) return;

    const fetchShift = async () => {
      try {
        const data = await getShiftById(shiftId);
        console.log('Fetched shift data:', data);

        // Parse date correctly to avoid timezone offset issues
        const parseLocalDate = (dateString: string) => {
          const date = new Date(dateString);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        setShiftRows([
          {
            start_date: parseLocalDate(data.date),
            start_time: data.start_time.slice(0, 5),
            end_time: data.end_time.slice(0, 5)
          }
        ]);

        setFormData({
          location: data.location,
          address: data.address,
          postcode: data.postcode || '',
          latitude: data.latitude,
          longitude: data.longitude,
          pay_rate: data.pay_rate,
          required_locums: data.required_locums,
          locum_role: data.locum_role,
          summary: data.summary || '',
          lunch_break_minutes: data.lunch_break_minutes,
          travel_expenses: data.travel_expenses
        } as any);
      } catch (err) {
        console.error(err);
      }
    };

    fetchShift();
  }, [shiftId]);

  // Fetch profile data to set default pharmacy address
  useEffect(() => {
    if (shiftId) return; // Skip if editing a shift

    const fetchProfileData = async () => {
      try {
        const profileData = await getProfile();
        // Set pharmacy address as default for the address field
        if (profileData?.profile?.address) {
          setFormData(prev => ({
            ...prev,
            address: profileData.profile.address || ''
          }));
        }
      } catch (err) {
        console.error('Failed to fetch profile data:', err);
      }
    };

    fetchProfileData();
  }, []);

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]:
        name === 'pay_rate' || name === 'required_locums' || name === 'lunch_break_minutes'
          ? value === '' ? '' : Number(value)  // ← Fixed: allows 0
          : value
    });
  };

  const handleLocationChange = useCallback((location: string) => {
    setFormData(prev => ({
      ...prev,
      location
    }));
  }, []);

  const handleCoordinatesChange = useCallback((coords: Coordinates) => {
    setFormData(prev => ({
      ...prev,
      latitude: coords.lat,
      longitude: coords.lng
    }));
  }, []);

  const addShiftRow = () => {
    setShiftRows(prev => [
      ...prev,
      {
        start_date: '',
        start_time: '',
        end_time: ''
      }
    ]);
  };

  const removeShiftRow = (index: number) => {
    setShiftRows(prev => prev.filter((_, i) => i !== index));
  };

  const handleShiftRowChange = (
    index: number,
    field: string,
    value: string
  ) => {
    const rows = [...shiftRows];

    rows[index] = {
      ...rows[index],
      [field]: value
    };

    setShiftRows(rows);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      setLoading(true);

      let res;

      if (shiftId) {
        const shift = shiftRows[0];

        res = await updateShift(shiftId, {
          ...formData,
          date: shift.start_date,
          start_time: shift.start_time,
          end_time: shift.end_time,
        });

      } else {

        let successCount = 0;

        for (const shift of shiftRows) {

          if (!shift.start_date || !shift.start_time || !shift.end_time) {
            continue;
          }

          if (shift.start_time >= shift.end_time) {
            throw new Error(
              `End time must be after start time for ${shift.start_date}`
            );
          }

          const shiftData = {
            date: shift.start_date,
            start_time: shift.start_time,
            end_time: shift.end_time,

            location: formData.location,
            address: formData.address,
            postcode: formData.postcode,
            latitude: formData.latitude,
            longitude: formData.longitude,

            pay_rate: formData.pay_rate,
            required_locums: formData.required_locums,
            locum_role: formData.locum_role,
            summary: formData.summary,
            lunch_break_minutes: formData.lunch_break_minutes,
            travel_expenses: formData.travel_expenses
          };

          const response = await createShift(shiftData);

          if (response.success) {
            successCount++;
          }
        }

        res = {
          success: successCount === shiftRows.length
        };

        if (res.success) {

          setSuccess(`${successCount} shift(s) created successfully`);

          setShowSuccessModal(true);

          setShiftRows([
            {
              start_date: '',
              start_time: '',
              end_time: ''
            }
          ]);

          setFormData({
            location: '',
            address: '',
            postcode: '',
            latitude: 0,
            longitude: 0,
            pay_rate: '',
            required_locums: '',
            locum_role: 'pharmacist',
            summary: '',
            lunch_break_minutes: '',
            travel_expenses: '',
          });
        }

        return;
      }

      if (res.success) {
        const message = shiftId ? 'Shift updated successfully' : 'Shift created successfully';
        setSuccess(message);
        setShowSuccessModal(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create shift');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SuccessModal
        show={showSuccessModal}
        title="Success!"
        message={success || 'Operation completed successfully'}
        onClose={() => setShowSuccessModal(false)}
        closeLabel="Close"
        closeDelay={2000}
      />

      {error && <Alert variant="danger">{error}</Alert>}

      <h3 className="mb-3">Create Shift</h3>

      <Card className="mb-3" >
        <Card.Body >
          <Form onSubmit={handleSubmit}>
            <Row className="g-3">

              {shiftRows.map((row, index) => (
                <Row key={index} className="mb-3 align-items-end">

                  <Col md={3}>
                    <Form.Group className="text-start">
                      <Form.Label>
                        Date <span className="text-danger">*</span>
                      </Form.Label>

                      <Form.Control
                        type="date"
                        value={row.start_date}
                        onChange={(e) =>
                          handleShiftRowChange(index, "start_date", e.target.value)
                        }
                        required
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="text-start">
                      <Form.Label>
                        Start Time <span className="text-danger">*</span>
                      </Form.Label>

                      <Form.Control
                        type="time"
                        value={row.start_time}
                        onChange={(e) =>
                          handleShiftRowChange(index, "start_time", e.target.value)
                        }
                        required
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="text-start">
                      <Form.Label>
                        End Time <span className="text-danger">*</span>
                      </Form.Label>

                      <Form.Control
                        type="time"
                        value={row.end_time}
                        onChange={(e) =>
                          handleShiftRowChange(index, "end_time", e.target.value)
                        }
                        required
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3} className="d-flex justify-content-end mt-2 gap-2">
                    {shiftRows.length > 1 && (
                      <Button
                        type="button"
                        variant="phoenix-danger"
                        onClick={() => removeShiftRow(index)}
                        startIcon={<FontAwesomeIcon icon={faTrash} />}
                      >
                        Remove
                      </Button>
                    )}

                    {index === shiftRows.length - 1 && (
                      <Button
                        type="button"
                        variant="phoenix-primary"
                        onClick={addShiftRow}
                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                      >
                        Add Shift
                      </Button>
                    )}
                  </Col>

                </Row>
              ))}

              <Col md={6}>
                <LocationAutocomplete
                  value={formData.location}
                  onChange={handleLocationChange}
                  onCoordinatesChange={handleCoordinatesChange}
                  label="Location"
                  placeholder="Search pharmacy location"
                  required
                />
              </Col>
              <Col md={6}>
                <Form.Group className="text-start">
                  <Form.Label>
                    Full Address <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="e.g., 123 High Street, London"
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="text-start">
                  <Form.Label>
                    Postcode <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="postcode"
                    value={formData.postcode}
                    onChange={handleChange}
                    placeholder="e.g., SW1A 1AA"
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="text-start">
                  <Form.Label>
                    Pay Rate <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="pay_rate"
                    value={formData.pay_rate}
                    onChange={handleChange}
                    placeholder="Enter hourly rate"
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="text-start">
                  <Form.Label>
                    Required Locums <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="required_locums"
                    value={formData.required_locums}
                    onChange={handleChange}
                    placeholder="e.g., 2"
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="text-start">
                  <Form.Label>
                    Required Locum Role <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="locum_role"
                    value={formData.locum_role}
                    onChange={handleChange}
                    required
                  >
                    <option value="pharmacist">Pharmacist</option>
                    <option value="technician">Technician</option>
                    <option value="dispenser">Dispenser</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="text-start">
                  <Form.Label>
                    Travel Expenses <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="travel_expenses"
                    value={formData.travel_expenses}
                    onChange={handleChange}
                    placeholder="e.g. 30p per mile, public transport etc"
                    min="0"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="text-start">
                  <Form.Label>
                    Lunch Break (minutes) <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="lunch_break_minutes"
                    value={formData.lunch_break_minutes}
                    onChange={handleChange}
                    placeholder="e.g., 30"
                    min="0"
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group className="text-start">
                  <Form.Label>
                    Description <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    name="summary"
                    value={formData.summary}
                    onChange={handleChange}
                    placeholder="Describe the shift requirements, duties, staffing levels, and any special instructions"
                    style={{ height: '100px' }}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Button
                  type="submit"
                  variant="phoenix-primary"
                  loading={loading}
                  startIcon={<FontAwesomeIcon icon={faSave} />}
                >
                  {shiftId ? 'Update Shift' : 'Create Shift'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};

export default PharmacistCreateShiftPage;
